import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const SecurityPoliciesManualPage = () => {
  useSEO({
    title: 'IT & Cybersecurity Policies Manual for Mortgage Companies | Mortgage Moat',
    description: 'Complete IT/cybersecurity policies & procedures manual for mortgage firms. 36+ GLBA and FFIEC-compliant policies, fully customizable, instant download for $395.',
    keywords: 'cybersecurity policy manual, IT security policies, GLBA policies, FFIEC compliance documentation, mortgage security procedures',
    canonical: '/security-policies-manual',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'IT/Cyber Security Policies & Procedures Manual',
        description: 'Comprehensive, customizable security policies and procedures manual covering all IT/cybersecurity best practices and GLBA/FFIEC regulatory compliance requirements for mortgage companies.',
        url: `${SITE_URL}/security-policies-manual`,
        brand: { '@type': 'Brand', name: 'Mortgage Moat' },
        offers: {
          '@type': 'Offer',
          price: '395',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: `${SITE_URL}/security-policies-manual`,
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Security Policies Manual', item: `${SITE_URL}/security-policies-manual` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Are the security policies specific to mortgage companies?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. While the policies cover universal security best practices, they\'re specifically tailored for mortgage companies with GLBA and FFIEC compliance requirements built in.' },
          },
          {
            '@type': 'Question',
            name: 'Can I customize the security policies for my organization?',
            acceptedAnswer: { '@type': 'Answer', text: 'Absolutely. The policies are provided in editable Microsoft Word format so you can easily customize them to match your organization\'s specific needs, structure, and processes.' },
          },
          {
            '@type': 'Question',
            name: 'Is there a money-back guarantee on the policy manual?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. We offer a 30-day money-back guarantee. If you\'re not satisfied with the policy manual for any reason, contact us for a full refund.' },
          },
        ],
      },
    ],
  });

  const features = [
    {
      icon: 'ri-file-shield-2-line',
      title: 'Comprehensive Coverage',
      description: 'Complete IT and cybersecurity policies covering all regulatory requirements and best practices'
    },
    {
      icon: 'ri-edit-box-line',
      title: 'Fully Customizable',
      description: 'Easily adapt policies to match your organization\'s specific needs, structure, and processes'
    },
    {
      icon: 'ri-checkbox-circle-line',
      title: 'Regulatory Compliance',
      description: 'Meets GLBA, FFIEC, and industry standards required for mortgage companies'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Regular Updates Included',
      description: 'Receive policy updates as regulations change and new threats emerge'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Ready to Implement',
      description: 'Professional formatting and clear language make deployment simple and immediate'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Implementation Guidance',
      description: 'Expert support to help you customize and deploy policies across your organization'
    }
  ];

  const policyCategories = [
    {
      category: 'Access Control & Authentication',
      policies: [
        'User Access Management Policy',
        'Password Policy & Requirements',
        'Multi-Factor Authentication Policy',
        'Privileged Access Management',
        'Account Provisioning & Deprovisioning',
        'Remote Access Security Policy'
      ]
    },
    {
      category: 'Data Protection & Privacy',
      policies: [
        'Data Classification Policy',
        'Data Encryption Standards',
        'Customer Data Protection Policy',
        'Data Retention & Disposal',
        'Privacy Policy & Procedures',
        'Personally Identifiable Information (PII) Handling'
      ]
    },
    {
      category: 'Network & Infrastructure Security',
      policies: [
        'Network Security Policy',
        'Firewall Management Policy',
        'Wireless Network Security',
        'Cloud Security Policy',
        'Endpoint Security Requirements',
        'Patch Management Policy'
      ]
    },
    {
      category: 'Incident Response & Business Continuity',
      policies: [
        'Incident Response Plan',
        'Security Incident Reporting Procedures',
        'Data Breach Response Policy',
        'Business Continuity Plan',
        'Disaster Recovery Procedures',
        'Emergency Communication Plan'
      ]
    },
    {
      category: 'Vendor & Third-Party Management',
      policies: [
        'Vendor Risk Management Policy',
        'Third-Party Security Requirements',
        'Vendor Due Diligence Procedures',
        'Service Level Agreement Standards',
        'Vendor Access Control Policy',
        'Supply Chain Security Policy'
      ]
    },
    {
      category: 'Employee Security & Awareness',
      policies: [
        'Acceptable Use Policy',
        'Security Awareness Training Policy',
        'Email & Communication Security',
        'Social Media Policy',
        'Bring Your Own Device (BYOD) Policy',
        'Clean Desk Policy'
      ]
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Save Months of Work',
      description: 'Skip the research and writing. Get professional policies ready to customize and deploy immediately.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Pass Audits with Confidence',
      description: 'Comprehensive documentation that meets examiner expectations and regulatory requirements.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Affordable Foundation',
      description: 'Get enterprise-level policies for a fraction of what consultants charge to create them.'
    },
    {
      icon: 'ri-team-line',
      title: 'Expert-Developed',
      description: 'Created by cybersecurity professionals who understand mortgage industry compliance.'
    }
  ];

  const process = [
    {
      step: '1',
      title: 'Purchase & Download',
      description: 'Instant access to the complete policy manual in editable format'
    },
    {
      step: '2',
      title: 'Review & Customize',
      description: 'Adapt policies to match your organization\'s specific needs and structure'
    },
    {
      step: '3',
      title: 'Management Approval',
      description: 'Present policies to leadership and board for review and approval'
    },
    {
      step: '4',
      title: 'Employee Distribution',
      description: 'Deploy policies to staff with acknowledgment tracking'
    },
    {
      step: '5',
      title: 'Training & Implementation',
      description: 'Train employees on policy requirements and begin enforcement'
    },
    {
      step: '6',
      title: 'Ongoing Maintenance',
      description: 'Receive updates and maintain policies as regulations evolve'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              SECURITY POLICIES & PROCEDURES
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Examiners Want Documentation? Get Compliant Fast
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Get a complete, customized security policies manual that meets GLBA and FFIEC requirements – delivered in days, not months.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 inline-block">
              <div className="text-6xl font-bold mb-2">$395</div>
              <div className="text-lg text-white/90">One-time payment • Lifetime access • Free updates</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/risk-assessment"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Purchase Now
                <i className="ri-shopping-cart-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Sample Policies
                <i className="ri-file-list-line"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why You Need This */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Every Mortgage Company Needs Security Policies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Regulatory examiners expect documented security policies. Don't get caught unprepared.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-close-circle-line text-red-600 text-2xl"></i>
                Without Proper Policies
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Fail regulatory examinations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Face compliance violations and fines</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Lack clear security standards</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Inconsistent security practices</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Higher insurance premiums</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-600 mt-1"></i>
                  <span>Increased liability exposure</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-green-600 text-2xl"></i>
                With Our Policy Manual
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Pass audits and examinations</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Meet GLBA and FFIEC requirements</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Clear security standards for all staff</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Consistent, enforceable procedures</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Reduced insurance costs</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-600 mt-1"></i>
                  <span>Protected from legal liability</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Makes Our Policy Manual Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not just generic templates – comprehensive, mortgage-specific policies ready to implement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${feature.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Complete Policy Coverage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              36+ comprehensive policies organized into six critical security categories.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {policyCategories.map((category, index) => (
              <div key={index} className="bg-gray-50 rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b-2 border-red-600">
                  {category.category}
                </h3>
                <ul className="space-y-3">
                  {category.policies.map((policy, policyIndex) => (
                    <li key={policyIndex} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-red-600 text-lg"></i>
                      </div>
                      <span className="text-gray-700 text-sm">{policy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our Policy Manual?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-4xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Simple Implementation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From purchase to full deployment in just a few steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 flex items-center justify-center flex-shrink-0 bg-red-600 text-white text-3xl font-bold rounded-full mb-6">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-2xl p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What's Included in Your Purchase
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[
                '36+ comprehensive security policies',
                'Fully editable Microsoft Word format',
                'Professional formatting and layout',
                'Clear, understandable language',
                'Mortgage industry-specific content',
                'GLBA and FFIEC compliance coverage',
                'Implementation guidance document',
                'Policy acknowledgment templates',
                'Customization instructions',
                'Regular updates for one year',
                'Email support for 90 days',
                'Instant download access'
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-green-500 text-xl"></i>
                  </div>
                  <span className="text-gray-700 font-semibold">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-xl mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                <i className="ri-gift-line text-blue-600 text-xl"></i>
                Bonus Materials Included
              </h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>Security awareness training outline</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>Incident response checklist</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>Vendor security questionnaire template</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-600 mt-1"></i>
                  <span>Security audit checklist</span>
                </li>
              </ul>
            </div>

            <div className="text-center">
              <div className="text-5xl font-bold text-red-600 mb-4">$395</div>
              <p className="text-gray-600 mb-6">One-time payment • Lifetime access • Free updates for one year</p>
              <a 
                href="/purchase/risk-assessment"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors w-full whitespace-nowrap cursor-pointer"
              >
                Purchase Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <p className="text-sm text-gray-500 mt-4">
                Instant download • 30-day money-back guarantee
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'Are these policies specific to mortgage companies?',
                answer: 'Yes. While the policies cover universal security best practices, they\'re specifically tailored for mortgage companies with GLBA and FFIEC compliance requirements built in.'
              },
              {
                question: 'Can I customize the policies for my organization?',
                answer: 'Absolutely. The policies are provided in editable Microsoft Word format so you can easily customize them to match your organization\'s specific needs, structure, and processes.'
              },
              {
                question: 'Do I need technical expertise to use these policies?',
                answer: 'No. The policies are written in clear, understandable language. We include implementation guidance to help you deploy them, and our support team is available to answer questions.'
              },
              {
                question: 'How do I receive the policy manual?',
                answer: 'Immediately after purchase, you\'ll receive a download link via email. You can download the complete policy manual in Microsoft Word format and start customizing right away.'
              },
              {
                question: 'What if regulations change?',
                answer: 'You receive free policy updates for one year after purchase. We monitor regulatory changes and update the policies accordingly, sending you the latest versions.'
              },
              {
                question: 'Can I get help implementing the policies?',
                answer: 'Yes. Your purchase includes 90 days of email support. For more comprehensive implementation assistance, we offer consulting services and our Complete Security Management subscription.'
              },
              {
                question: 'Is there a money-back guarantee?',
                answer: 'Yes. We offer a 30-day money-back guarantee. If you\'re not satisfied with the policy manual for any reason, contact us for a full refund.'
              },
              {
                question: 'Can I share these policies with other companies?',
                answer: 'The policies are licensed for use by a single organization. If you need to use them for multiple companies, please contact us about multi-organization licensing.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Build Your Security Foundation Today
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Get comprehensive, professional security policies ready to customize and deploy immediately.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/risk-assessment"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Purchase for $395
              <i className="ri-shopping-cart-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              View Sample Policies
              <i className="ri-file-list-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            Questions? Call us at (555) 123-4567 or email info@mortgagemoat.com
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SecurityPoliciesManualPage;